/**
 * Grounding for Inky: resolves the active project/document/session from the route, builds
 * numbered requirement / meeting-note / project-file evidence for document drafting and
 * edits, and runs the once-per-turn project retrieval (RAG) for chat turns.
 */

import { supabase } from "../lib/supabase";
import edgeFunctionService from "../services/edgeFunctionService";
import { oneLine } from "./inkyPersona";
import { workspaceDocumentId } from "./routeContext";

/** Requirements included in drafting evidence. */
const MAX_REQUIREMENTS = 40;
/** Meeting summaries included in drafting evidence. */
const MAX_SESSIONS = 3;
/** Characters per meeting summary. */
const SESSION_SUMMARY_CHARS = 1500;
/** Characters of retrieved project-file context per chat turn. */
const MAX_RETRIEVED_CHARS = 8000;

/**
 * Find the project that owns a generated document.
 * @param {Array<object>} projects - AppContext projects.
 * @param {string} documentId - Document id.
 * @returns {{project: object, document: object}|null} Owner and document.
 */
export const findDocumentProject = (projects, documentId) => {
  if (!documentId) return null;
  for (const project of projects || []) {
    const document = (project.generatedDocuments || []).find((doc) => doc.id === documentId);
    if (document) return { project, document };
  }
  return null;
};

/**
 * Find the project that owns a session.
 * @param {Array<object>} projects - AppContext projects.
 * @param {string} sessionId - Session id.
 * @returns {{project: object, session: object}|null} Owner and session.
 */
export const findSessionProject = (projects, sessionId) => {
  if (!sessionId) return null;
  for (const project of projects || []) {
    const session = (project.sessions || []).find((s) => s.id === sessionId);
    if (session) return { project, session };
  }
  return null;
};

/**
 * Resolve what the user is looking at from the route.
 * @param {{pathname: string, search: string, contextData: object}} params - Route + data.
 * @returns {{kind: string, project: object|null, document: object|null, template: object|null, session: object|null, customer: object|null}} Active entities.
 */
export const resolveActiveContext = ({ pathname, search, contextData }) => {
  const projects = contextData?.projects || [];
  const parts = String(pathname || "").split("/").filter(Boolean);
  const empty = { kind: "general", project: null, document: null, template: null, session: null, customer: null };

  const workspaceDocument = workspaceDocumentId(pathname);
  if (pathname === "/editor" || (workspaceDocument && workspaceDocument !== "new")) {
    const documentId = workspaceDocument || new URLSearchParams(search || "").get("documentId");
    const found = findDocumentProject(projects, documentId);
    return {
      ...empty,
      kind: "document",
      project: found?.project || null,
      document: found?.document || (documentId ? { id: documentId, name: "Document" } : null),
    };
  }
  if (parts[0] === "templates" && parts[1]) {
    const templateId = parts[1] === "powerpoint-rich" ? parts[2] : parts[1];
    const template = (contextData?.templates || []).find((t) => t.id === templateId) || null;
    return { ...empty, kind: template || parts.includes("edit") ? "template" : "general", template };
  }
  if (parts[0] === "customers" && parts[1] === "sessions" && parts[2]) {
    const found = findSessionProject(projects, parts[2]);
    return {
      ...empty,
      kind: "session",
      project: found?.project || null,
      session: found?.session || null,
      customer: found?.project
        ? (contextData?.customers || []).find((c) => c.id === found.project.customer_id) || null
        : null,
    };
  }
  if (parts[0] === "projects" && parts[1]) {
    return { ...empty, kind: "project", project: projects.find((p) => p.id === parts[1]) || null };
  }
  if (parts[0] === "customers" && parts[1] && parts[1] !== "sessions") {
    return { ...empty, kind: "customer", customer: (contextData?.customers || []).find((c) => c.id === parts[1]) || null };
  }
  return empty;
};

/**
 * Rank a requirement priority (lower is more important).
 * @param {string|number|null} priority - Raw priority.
 * @returns {number} Rank.
 */
const priorityRank = (priority) => {
  if (typeof priority === "number") return priority;
  const value = String(priority || "").toLowerCase();
  if (/critical|must|p0|highest|urgent/.test(value)) return 0;
  if (/high|p1/.test(value)) return 1;
  if (/medium|should|p2|normal/.test(value)) return 2;
  if (/low|could|p3/.test(value)) return 3;
  if (/won't|wont|p4|lowest/.test(value)) return 4;
  return 2.5;
};

/**
 * Requirements ordered by priority, then newest first (stable for determinism).
 * @param {Array<object>} requirements - Project requirements.
 * @returns {Array<object>} Sorted copy.
 */
export const sortRequirements = (requirements) =>
  [...(requirements || [])].sort((a, b) => {
    const byPriority = priorityRank(a.priority) - priorityRank(b.priority);
    if (byPriority) return byPriority;
    return String(a.id).localeCompare(String(b.id));
  });

/**
 * Load the latest session_insights summaries for sessions (best effort).
 * @param {Array<string>} sessionIds - Session ids.
 * @returns {Promise<Record<string, {summary: string, actions: Array}>>} Insights by session id.
 */
export const loadSessionInsights = async (sessionIds) => {
  const ids = (sessionIds || []).filter(Boolean);
  if (!ids.length) return {};
  try {
    const { data, error } = await supabase
      .from("session_insights")
      .select("session_id, summary, actions, updated_at")
      .in("session_id", ids);
    if (error) throw error;
    const map = {};
    for (const row of data || []) map[row.session_id] = row;
    return map;
  } catch (error) {
    console.warn("[inky] session insights unavailable", error);
    return {};
  }
};

/**
 * Text form of a session summary (summaries are strings or structured objects).
 * @param {*} summary - Raw summary.
 * @returns {string} Text.
 */
const summaryText = (summary) => {
  if (!summary) return "";
  if (typeof summary === "string") return summary;
  if (typeof summary === "object") {
    return summary.overview || summary.summary || summary.text || JSON.stringify(summary);
  }
  return String(summary);
};

/**
 * Format retrieved project-file matches as a prompt section.
 * @param {Array<object>} matches - retrieve-project-context results.
 * @param {number} maxChars - Character cap.
 * @returns {string} Section text.
 */
export const formatRetrievedMatches = (matches, maxChars = MAX_RETRIEVED_CHARS) => {
  if (!Array.isArray(matches) || !matches.length) return "";
  return matches
    .map((match, index) => {
      const citation = match.citation || match.fileName || `File ${index + 1}`;
      return `[C${index + 1}] ${citation}${match.sourceType ? ` (${match.sourceType})` : ""}\n${String(match.content || "").trim()}`;
    })
    .join("\n\n")
    .slice(0, maxChars);
};

/**
 * Whether a message asks about uploaded images.
 * @param {string} message - User message.
 * @returns {boolean} True when image context should be attached.
 */
export const wantsImageContext = (message) =>
  /\b(image|screenshot|picture|photo|diagram|chart|visual|see in|shown in|look at)\b/i.test(String(message || ""));

/**
 * Read a Blob as base64 (no data-URL prefix).
 * @param {Blob} blob - Image blob.
 * @returns {Promise<string>} Base64 bytes.
 */
const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const value = String(reader.result || "");
      resolve(value.includes(",") ? value.split(",").pop() : value);
    };
    reader.onerror = () => reject(new Error("Failed to read image."));
    reader.readAsDataURL(blob);
  });

/**
 * Inline Gemini image parts from signed storage URLs (max 2 images, 5 MB each).
 * @param {Array<object>} images - Retrieved image metadata.
 * @returns {Promise<Array<object>>} inlineData parts.
 */
const buildImageParts = async (images) => {
  const parts = [];
  for (const image of (images || []).slice(0, 2)) {
    if (!image?.signedUrl || !String(image.mimeType || "").startsWith("image/")) continue;
    try {
      const response = await fetch(image.signedUrl);
      if (!response.ok) continue;
      const blob = await response.blob();
      if (blob.size > 5 * 1024 * 1024) continue;
      parts.push({ inlineData: { mimeType: image.mimeType, data: await blobToBase64(blob) } });
    } catch (error) {
      console.warn("[inky] could not attach image context", error);
    }
  }
  return parts;
};

/**
 * Retrieve project-file context ONCE for a chat turn, keyed on the user's own message
 * (never a tool result), with images only when the message asks about visuals.
 * @param {{projectId: string|null, query: string}} params - Retrieval inputs.
 * @returns {Promise<{section: string, imageParts: Array<object>}>} Prompt section and image parts.
 */
export const retrieveForTurn = async ({ projectId, query }) => {
  const text = String(query || "").trim();
  if (!projectId || text.length < 3) return { section: "", imageParts: [] };
  try {
    const includeImages = wantsImageContext(text);
    const result = await edgeFunctionService.retrieveProjectContext({
      projectId,
      query: text.slice(0, 2000),
      matchCount: 8,
      ...(includeImages ? { includeImages } : {}),
    });
    const section = formatRetrievedMatches(result?.results || result?.matches || []);
    const imageParts = includeImages ? await buildImageParts(result?.images || []) : [];
    return { section, imageParts };
  } catch (error) {
    console.warn("[inky] project retrieval failed", error);
    return { section: "", imageParts: [] };
  }
};

/**
 * Build numbered evidence for drafting/editing a project document: requirements (R#),
 * meeting notes (S#) and retrieved project files (C#). The model must use ONLY these facts.
 * @param {{project: object|null, query: string, includeFiles?: boolean}} params - Inputs.
 * @returns {Promise<{text: string, evidence: Record<string, string>, counts: {requirements: number, sessions: number, files: number}}>} Grounding.
 */
export const buildProjectGrounding = async ({ project, query, includeFiles = true }) => {
  if (!project) return { text: "", evidence: {}, counts: { requirements: 0, sessions: 0, files: 0 } };
  const evidence = {};
  const lines = [`PROJECT: ${project.name}${project.description ? ` — ${oneLine(project.description, 400)}` : ""}`];

  const requirements = sortRequirements(project.requirements).slice(0, MAX_REQUIREMENTS);
  if (requirements.length) {
    lines.push("", "PROJECT REQUIREMENTS:");
    requirements.forEach((req, index) => {
      const label = `R${index + 1}`;
      evidence[label] = req.id;
      const meta = [req.type, req.priority].filter(Boolean).join(", ");
      const criteria = Array.isArray(req.acceptanceCriteria) && req.acceptanceCriteria.length
        ? ` Acceptance: ${req.acceptanceCriteria.map((c) => (typeof c === "string" ? c : c?.text || c?.description || "")).filter(Boolean).join("; ")}`
        : "";
      lines.push(`[${label}] ${req.title || "Untitled"}${meta ? ` (${meta})` : ""}: ${oneLine(req.description, 500)}${oneLine(criteria, 400)}`);
    });
  }

  const sessions = [...(project.sessions || [])]
    .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")))
    .slice(0, MAX_SESSIONS);
  const [insights, retrieved] = await Promise.all([
    loadSessionInsights(sessions.map((s) => s.id)),
    includeFiles && query ? retrieveForTurn({ projectId: project.id, query }) : Promise.resolve({ section: "" }),
  ]);
  const sessionLines = [];
  sessions.forEach((session, index) => {
    const text = summaryText(insights[session.id]?.summary || session.summary);
    if (!text) return;
    const label = `S${index + 1}`;
    evidence[label] = session.id;
    sessionLines.push(`[${label}] ${session.name}${session.createdAt ? ` (${String(session.createdAt).slice(0, 10)})` : ""}: ${oneLine(text, SESSION_SUMMARY_CHARS)}`);
  });
  if (sessionLines.length) lines.push("", "MEETING NOTES:", ...sessionLines);
  if (retrieved.section) lines.push("", "PROJECT FILES:", retrieved.section);

  return {
    text: lines.join("\n"),
    evidence,
    counts: {
      requirements: requirements.length,
      sessions: sessionLines.length,
      files: retrieved.section ? (retrieved.section.match(/^\[C\d+\]/gm) || []).length : 0,
    },
  };
};
